import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import EmailCampaignAudienceStep from './components/EmailCampaignAudienceStep';
import EmailCampaignScheduleStep from './components/EmailCampaignScheduleStep';
import MobileNotificationContentStep from './components/MobileNotificationContentStep';
import MobileNotificationReviewStep from './components/MobileNotificationReviewStep';
import recurrenceSummary from './utils/recurrenceSummary';
import {
  createMobileNotificationCampaign,
  estimateMobileNotificationRecipients,
  getMobileNotificationCampaign,
  getMobileNotificationLookups,
  mapLaravelError,
  scheduleMobileNotificationCampaign,
  searchMobileNotificationEmployees,
  updateMobileNotificationCampaign,
} from '../../services/mobileNotificationCampaignsApi';

const STEPS = ['Audience', 'Content', 'Schedule', 'Review'];

const toInputDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const parseJson = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  return JSON.parse(raw);
};

const normalizeCampaign = (item = {}) => ({
  id: item.id,
  title: item.title || '',
  message: item.message || '',
  status: item.status || 'draft',
  audience_mode: item.audience_mode || 'all',
  audience_rules: item.audience_rules || { active_only: true },
  data: item.data || null,
  data_json: item.data ? JSON.stringify(item.data, null, 2) : '',
  timezone: item.timezone || 'Asia/Riyadh',
  send_mode: item.send_mode || 'once',
  send_at: toInputDateTime(item.send_at),
  recurrence: item.recurrence || { frequency: 'daily', interval: 1, end: { type: 'never' } },
});

const toPayload = (campaign) => ({
  title: campaign.title,
  message: campaign.message,
  audience_mode: campaign.audience_mode,
  audience_rules: campaign.audience_rules || {},
  data: parseJson(campaign.data_json),
  timezone: campaign.timezone || 'Asia/Riyadh',
});

const unwrapCampaign = (payload) => {
  if (payload && typeof payload === 'object' && !Array.isArray(payload) && payload.id != null) {
    return payload;
  }

  if (payload?.data && typeof payload.data === 'object' && !Array.isArray(payload.data) && payload.data.id != null) {
    return payload.data;
  }

  return payload || {};
};

export default function MobileNotificationBuilderPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const routeId = id ? Number(id) : null;

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [lookups, setLookups] = useState({});
  const [campaign, setCampaign] = useState(normalizeCampaign());
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [scheduleFieldErrors, setScheduleFieldErrors] = useState({});
  const [estimatedRecipients, setEstimatedRecipients] = useState(0);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [baseEmployeeOptions, setBaseEmployeeOptions] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');

  const selectedEmployeeIds = useMemo(
    () => (
      Array.isArray(campaign?.audience_rules?.employee_ids)
        ? campaign.audience_rules.employee_ids.filter(Boolean).map((value) => Number(value))
        : []
    ),
    [campaign?.audience_rules?.employee_ids]
  );
  const selectedEmployeesRef = useRef(selectedEmployees);
  const selectedEmployeeIdsRef = useRef(selectedEmployeeIds);

  const isReadOnly = ['sending', 'sent', 'cancelled'].includes(String(campaign?.status || '').toLowerCase());

  useEffect(() => {
    selectedEmployeesRef.current = selectedEmployees;
  }, [selectedEmployees]);

  useEffect(() => {
    selectedEmployeeIdsRef.current = selectedEmployeeIds;
  }, [selectedEmployeeIds]);

  const refreshCampaign = useCallback(async (campaignId, defaults = {}) => {
    const payload = await getMobileNotificationCampaign(campaignId);
    const campaignData = unwrapCampaign(payload);
    const normalized = normalizeCampaign(campaignData);
    setCampaign((prev) => ({
      ...normalized,
      send_mode: prev?.send_mode || normalized.send_mode,
      send_at: prev?.send_at || normalized.send_at,
      recurrence: prev?.recurrence || normalized.recurrence,
      timezone: prev?.timezone || normalized.timezone || defaults?.timezone || 'Asia/Riyadh',
    }));
    setEstimatedRecipients(Number(campaignData?.recipient_snapshot_count || 0));
    if (Array.isArray(normalized?.audience_rules?.employee_ids)) {
      setSelectedEmployees(
        normalized.audience_rules.employee_ids.map((employeeId) => ({
          id: employeeId,
          name: `Employee #${employeeId}`,
        }))
      );
    }
  }, []);

  const init = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const lookupData = await getMobileNotificationLookups();
      setLookups(lookupData || {});

      if (routeId) {
        await refreshCampaign(routeId, lookupData?.defaults || {});
      } else {
        const created = await createMobileNotificationCampaign({
          title: '',
          message: '',
          audience_mode: 'all',
          audience_rules: { active_only: true },
          timezone: lookupData?.defaults?.timezone || 'Asia/Riyadh',
        });

        setCampaign(normalizeCampaign(unwrapCampaign(created)));
      }
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setLoading(false);
    }
  }, [refreshCampaign, routeId]);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    if (!campaign?.id) return undefined;

    const timer = window.setTimeout(async () => {
      setEstimateLoading(true);
      try {
        const payload = await estimateMobileNotificationRecipients(campaign.id, {
          audience_mode: campaign.audience_mode,
          audience_rules: campaign.audience_rules || {},
        });
        setEstimatedRecipients(Number(payload?.estimated_recipients || 0));
      } catch {
        setEstimatedRecipients(0);
      } finally {
        setEstimateLoading(false);
      }
    }, 400);

    return () => window.clearTimeout(timer);
  }, [campaign.id, campaign.audience_mode, campaign.audience_rules]);

  const mergeUniqueEmployees = useCallback((items, existing = []) => {
    const map = new Map();
    [...existing, ...(Array.isArray(items) ? items : [])].forEach((item) => {
      if (item?.id == null) return;
      map.set(item.id, item);
    });
    return Array.from(map.values());
  }, []);

  const handleSearchEmployees = useCallback(async (query = '', extraParams = {}) => {
    setEmployeeLoading(true);
    try {
      const payload = await searchMobileNotificationEmployees({
        q: query,
        per_page: 30,
        ...extraParams,
      });
      const items = Array.isArray(payload?.data) ? payload.data : [];
      const selectedIds = new Set(selectedEmployeeIdsRef.current);
      const selectedItems = Array.isArray(selectedEmployeesRef.current)
        ? selectedEmployeesRef.current.filter((item) => selectedIds.has(Number(item?.id)))
        : [];

      if (Array.isArray(extraParams?.employee_ids) && extraParams.employee_ids.length) {
        setEmployeeOptions((prev) => mergeUniqueEmployees(items, prev));
      } else if (String(query || '').trim() === '') {
        setBaseEmployeeOptions(items);
        setEmployeeOptions((prev) => mergeUniqueEmployees([...items, ...selectedItems], prev));
      } else {
        setEmployeeOptions((prev) => {
          const preserved = prev.filter((item) => selectedIds.has(Number(item?.id)));
          return mergeUniqueEmployees([...items, ...selectedItems], preserved);
        });
      }
    } catch {
      if (!Object.keys(extraParams || {}).length) {
        setEmployeeOptions([]);
      }
    } finally {
      setEmployeeLoading(false);
    }
  }, [mergeUniqueEmployees]);

  const handleEmployeeSearchInputChange = useCallback((query = '', reason = 'input') => {
    if (reason === 'clear') {
      setEmployeeSearchQuery('');
      return;
    }

    if (reason !== 'input') {
      return;
    }

    setEmployeeSearchQuery(query);
  }, []);

  useEffect(() => {
    if (campaign?.audience_mode !== 'by_employee') return;
    if (baseEmployeeOptions.length > 0) return;

    handleSearchEmployees('');
  }, [baseEmployeeOptions.length, campaign?.audience_mode, handleSearchEmployees]);

  useEffect(() => {
    if (campaign?.audience_mode !== 'by_employee') {
      return undefined;
    }

    const trimmed = String(employeeSearchQuery || '').trim();
    if (trimmed === '') {
      setEmployeeOptions((prev) => {
        const selectedIds = new Set(selectedEmployeeIds);
        const selectedItems = selectedEmployees.filter((item) => selectedIds.has(Number(item?.id)));
        const preserved = prev.filter((item) => selectedIds.has(Number(item?.id)));
        return mergeUniqueEmployees([...baseEmployeeOptions, ...selectedItems], preserved);
      });
      return undefined;
    }

    const timer = window.setTimeout(() => {
      handleSearchEmployees(trimmed);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [
    baseEmployeeOptions,
    campaign?.audience_mode,
    employeeSearchQuery,
    handleSearchEmployees,
    mergeUniqueEmployees,
    selectedEmployeeIds,
    selectedEmployees,
  ]);

  useEffect(() => {
    if (!selectedEmployeeIds.length) {
      if (selectedEmployees.length > 0) {
        setSelectedEmployees([]);
      }
      return;
    }

    const resolved = selectedEmployeeIds
      .map((employeeId) => employeeOptions.find((item) => Number(item.id) === Number(employeeId)))
      .filter(Boolean);
    const sameSelection = resolved.length === selectedEmployees.length
      && resolved.every((item, index) => Number(item.id) === Number(selectedEmployees[index]?.id));

    if (resolved.length === selectedEmployeeIds.length) {
      if (!sameSelection) {
        setSelectedEmployees(resolved);
      }
      return;
    }

    handleSearchEmployees('', { employee_ids: selectedEmployeeIds });
  }, [employeeOptions, handleSearchEmployees, selectedEmployeeIds, selectedEmployees]);

  const saveDraft = useCallback(async () => {
    if (!campaign?.id) return null;

    setSaving(true);
    setError('');
    setFieldErrors({});

    try {
      let payload;
      try {
        payload = toPayload(campaign);
      } catch {
        setError('Metadata JSON must be valid.');
        setFieldErrors({ data_json: 'Metadata JSON must be valid.' });
        throw new Error('invalid-json');
      }

      const response = await updateMobileNotificationCampaign(campaign.id, payload);
      const normalized = normalizeCampaign(unwrapCampaign(response));
      setCampaign((prev) => ({
        ...normalized,
        send_mode: prev.send_mode,
        send_at: prev.send_at,
        recurrence: prev.recurrence,
        timezone: prev.timezone || normalized.timezone,
      }));
      return normalized;
    } catch (requestError) {
      if (requestError?.message === 'invalid-json') {
        throw requestError;
      }

      const mapped = mapLaravelError(requestError);
      setError(mapped.message);
      setFieldErrors(mapped.fieldErrors);
      throw requestError;
    } finally {
      setSaving(false);
    }
  }, [campaign]);

  const nextStep = async () => {
    if (!isReadOnly) {
      try {
        await saveDraft();
      } catch {
        return;
      }
    }
    setStep((prev) => Math.min(STEPS.length - 1, prev + 1));
  };

  const previousStep = () => setStep((prev) => Math.max(0, prev - 1));

  const handleConfirmSchedule = async () => {
    setConfirmLoading(true);
    setError('');
    setScheduleFieldErrors({});

    try {
      if (!isReadOnly) {
        await saveDraft();
      }

      const response = await scheduleMobileNotificationCampaign(campaign.id, {
        send_mode: campaign.send_mode,
        timezone: campaign.timezone || 'Asia/Riyadh',
        send_at: campaign.send_at || null,
        recurrence: campaign.send_mode === 'recurring' ? campaign.recurrence : null,
      });

      const nextCampaign = normalizeCampaign(unwrapCampaign(response?.campaign));
      setCampaign(nextCampaign);
      navigate(`/admin/communications/mobile-notifications/${campaign.id}`);
    } catch (requestError) {
      if (requestError?.message === 'invalid-json') {
        return;
      }

      const mapped = mapLaravelError(requestError);
      setError(mapped.message);
      setScheduleFieldErrors(mapped.fieldErrors);
    } finally {
      setConfirmLoading(false);
    }
  };

  const summary = useMemo(() => recurrenceSummary(campaign), [campaign]);

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack spacing={0.5}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {routeId ? 'Edit Mobile Notification' : 'New Mobile Notification'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              HR-admin campaigns for mobile inbox plus Expo push. These notifications are hidden from the web notification center.
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button onClick={() => navigate('/admin/communications/mobile-notifications')}>Back</Button>
          </Stack>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stepper activeStep={step} alternativeLabel>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          {step === 0 ? (
            <EmailCampaignAudienceStep
              value={campaign}
              onChange={setCampaign}
              lookups={lookups}
              selectedEmployees={selectedEmployees}
              onSelectedEmployeesChange={setSelectedEmployees}
              employeeOptions={employeeOptions}
              onEmployeeSearch={handleEmployeeSearchInputChange}
              employeeLoading={employeeLoading}
              estimatedRecipients={estimatedRecipients}
              estimateLoading={estimateLoading}
              readOnly={isReadOnly}
              campaignType="mobile"
            />
          ) : null}

          {step === 1 ? (
            <MobileNotificationContentStep
              value={campaign}
              onChange={setCampaign}
              readOnly={isReadOnly}
              fieldErrors={fieldErrors}
            />
          ) : null}

          {step === 2 ? (
            <EmailCampaignScheduleStep
              value={campaign}
              onChange={setCampaign}
              readOnly={isReadOnly}
              fieldErrors={scheduleFieldErrors}
            />
          ) : null}

          {step === 3 ? (
            <MobileNotificationReviewStep
              campaign={campaign}
              estimatedRecipients={estimatedRecipients}
              scheduleSummary={summary}
            />
          ) : null}
        </Paper>

        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button onClick={previousStep} disabled={step === 0}>
            Back
          </Button>

          <Stack direction="row" spacing={1}>
            {step < STEPS.length - 1 ? (
              <Button variant="contained" onClick={nextStep} disabled={saving}>
                {saving ? 'Saving…' : 'Save & Next'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleConfirmSchedule}
                disabled={confirmLoading || isReadOnly}
              >
                {confirmLoading ? 'Submitting…' : 'Confirm Schedule'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
